import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { CreationTime } from "@/components/CreationTime";
import { Button } from "@/components/flexkit/Button";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { Text } from "@/components/podkit/typography/Text";
import { useCurrentMember } from "@/hooks/use-current-member";
import { useMember } from "@/hooks/use-member";
import { type PlainPersonalAccessToken } from "@/queries/personal-access-tokens-queries";
import { getValidityDuration } from "@/routes/settings/user/personal-access-tokens/time-format";
import { type FC } from "react";

export const PersonalAccessTokensList: FC<{
    tokens: PlainPersonalAccessToken[];
    onNew: () => void;
    onDelete: (pat: PlainPersonalAccessToken) => void;
}> = ({ tokens, onNew, onDelete }) => {
    const hasPATs = tokens.length > 0;
    return (
        <div data-testid="personal-access-tokens-list">
            <div className="flex flex-col flex-wrap items-center gap-4">
                {tokens.map((pat) => (
                    <PersonalAccessTokenRow key={pat.id} pat={pat} onDelete={() => onDelete(pat)} />
                ))}
            </div>
            {!hasPATs && (
                <EmptyStateCard
                    title="No Personal Access Tokens"
                    description="Create a new token to integrate with other systems."
                    data-testid="personal-access-tokens-empty-state"
                >
                    <Button
                        onClick={() => onNew()}
                        size="md"
                        variant="primary"
                        data-testid="new-token-button-empty-state"
                        data-tracking-id="new-token-personal-access-token-list"
                    >
                        New Token
                    </Button>
                </EmptyStateCard>
            )}
        </div>
    );
};

const PersonalAccessTokenRow: FC<{
    pat: PlainPersonalAccessToken;
    onDelete: () => void;
}> = ({ pat, onDelete }) => {
    const { currentMember } = useCurrentMember();
    const { member: creator } = useMember(pat.creator?.id);
    return (
        <div
            className="group inline-flex h-14 w-full items-center justify-start gap-4 rounded-xl border border-border-base bg-surface-glass px-4 py-2.5 text-xs transition-shadow duration-200 hover:shadow"
            data-testid={pat.id}
        >
            <div className="min-w-6/12 max-w-6/12 flex w-6/12 flex-col truncate">
                <Text className="truncate text-base font-bold text-content-primary">{pat.description}</Text>
                {currentMember && creator && (
                    <Text className="truncate text-sm text-content-secondary">
                        by{" "}
                        {currentMember?.userId === creator?.userId ? (
                            <span>You</span>
                        ) : (
                            creator?.fullName || <span>Unknown</span>
                        )}
                        {" · "}
                        <CreationTime timestamp={pat.createdAt} />
                    </Text>
                )}
            </div>
            <div className="max-w-3/12 flex w-3/12 flex-col items-start justify-start truncate">
                <div className="truncate text-base text-content-secondary">{getValidityDuration(pat)}</div>
            </div>
            <div className="max-w-4/12 flex w-3/12 items-end justify-end gap-2">
                <Button
                    variant={"ghost"}
                    onClick={onDelete}
                    LeadingIcon={IconTrash}
                    aria-label="Delete personal access token"
                    data-testid={`delete-personal-access-token-${pat.id}`}
                    data-tracking-id="delete-token-personal-access-token-row"
                />
            </div>
        </div>
    );
};
